/**
 * SSO protocol step — infer SAML/OIDC from references and guide unsure users.
 */
import {
  isConversationalAssistInput,
  isUnsureInput,
  resolveReferenceText,
  shouldTryReferenceInference,
  buildAgentContextHeader,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';

export {
  isConversationalAssistInput,
  resolveReferenceText,
  shouldTryReferenceInference,
} from './vysorWizardAssistCommon';

/** @deprecated Use isUnsureInput from vysorWizardAssistCommon */
export const isSsoUnsureInput = isUnsureInput;

export type InferredSsoProtocol = 'SAML' | 'OIDC';

export interface SsoProtocolInference {
  protocol: InferredSsoProtocol;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

export function inferSsoProtocolFromText(text: string): SsoProtocolInference | null {
  const sample = text.slice(0, 12000);
  const lower = sample.toLowerCase();

  const samlSignals = [
    /entitydescriptor/i.test(sample),
    /assertionconsumerservice/i.test(sample),
    /\bsaml\b/i.test(sample),
    /federationmetadata/i.test(lower),
    /metadata\.xml/i.test(lower),
    /x509certificate/i.test(sample),
    /urn:oasis:names:tc:saml/i.test(sample),
  ].filter(Boolean).length;

  const oidcSignals = [
    /openid-connect/i.test(lower),
    /openid_configuration/i.test(lower),
    /\.well-known\/openid-configuration/i.test(lower),
    /\boidc\b/i.test(lower),
    /authorization_code/i.test(lower),
    /client_id/i.test(lower),
    /client_secret/i.test(lower),
    /oauth2/i.test(lower),
    /redirect_uri/i.test(lower),
  ].filter(Boolean).length;

  if (samlSignals >= 2 && samlSignals > oidcSignals) {
    return {
      protocol: 'SAML',
      confidence: samlSignals >= 3 ? 'high' : 'medium',
      reason: 'Reference contains SAML metadata patterns (EntityDescriptor, ACS, or SAML XML).',
    };
  }

  if (oidcSignals >= 2 && oidcSignals > samlSignals) {
    return {
      protocol: 'OIDC',
      confidence: oidcSignals >= 3 ? 'high' : 'medium',
      reason: 'Reference mentions OIDC/OAuth patterns (client ID, discovery URL, or authorization code).',
    };
  }

  if (/\bsaml\b/i.test(lower) && !/\boidc\b/i.test(lower)) {
    return { protocol: 'SAML', confidence: 'low', reason: 'Text mentions SAML.' };
  }
  if (/\boidc\b/i.test(lower) || /\bopenid\b/i.test(lower)) {
    return { protocol: 'OIDC', confidence: 'low', reason: 'Text mentions OIDC/OpenID.' };
  }

  return null;
}

/** @deprecated Kept for tests — protocol help always goes to the model. */
export function buildSsoProtocolAssistMessage(appName?: string, instanceName?: string): string {
  const target =
    appName && instanceName
      ? `${appName} / ${instanceName}`
      : appName || 'this app';
  return `[deprecated] Protocol help for ${target} is handled by the AI assistant.`;
}

/** @deprecated Prefer routing any free text to the model; kept for tests. */
export function wantsProtocolStepGuidance(raw: string, optionValue?: string): boolean {
  if (optionValue === 'Not sure') return true;
  return isUnsureInput(raw) || (!!raw.trim() && !/^(saml|oidc)$/i.test(raw.trim()));
}

export function buildWizardContextForProtocolAssist(
  collected: Record<string, string>,
  userMessage: string,
  attachmentText?: string,
): string {
  const lines = [
    'User is on Configure SSO — protocol step (SAML vs OIDC).',
    `Application: ${collected.application_name || 'unknown'} (ID: ${collected.application_id || 'pending'})`,
    `Environment: ${collected.instance_name || 'unknown'} (ID: ${collected.instance_id || 'pending'})`,
    '',
    DYNAMIC_AI_REPLY_RULES,
    'Prefer SAML for Workday / enterprise HR when a recommendation helps — one-line reason is enough.',
    'If confident, call create_sso_request with sso_type, applicationId, and instanceId from above.',
    '',
    `User: ${userMessage}`,
  ];
  const header = buildAgentContextHeader('Configure SSO — protocol step', lines);
  if (attachmentText?.trim()) {
    return `${header}\n[Reference material]\n${attachmentText.slice(0, 6000)}`;
  }
  return header;
}
